package com.ijoin.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Visibility
import androidx.compose.material.icons.filled.VisibilityOff
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import com.ijoin.data.repository.AuthRepository
import com.ijoin.i18n.strings
import com.ijoin.ui.design.EventCardAccentDark
import com.ijoin.ui.design.EventCardAlert
import com.ijoin.ui.design.EventCardBackground
import com.ijoin.ui.design.EventCardInputCorner
import com.ijoin.ui.design.EventPrimaryButton
import com.ijoin.ui.design.EventSurfaceCard
import com.ijoin.ui.design.EventTextAction
import com.ijoin.ui.design.eventBackgroundBrush
import com.ijoin.ui.design.eventOutlinedFieldColors
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PasswordResetScreen(
    initialToken: String?,
    onNavigateBack: () -> Unit,
    authRepository: AuthRepository = remember { AuthRepository() }
) {
    val strings = strings()
    val scope = rememberCoroutineScope()

    var email by remember { mutableStateOf("") }
    var token by remember { mutableStateOf(initialToken.orEmpty()) }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var requestMessage by remember { mutableStateOf<String?>(null) }
    var resetMessage by remember { mutableStateOf<String?>(null) }
    var requestError by remember { mutableStateOf<String?>(null) }
    var resetError by remember { mutableStateOf<String?>(null) }
    var isRequestLoading by remember { mutableStateOf(false) }
    var isResetLoading by remember { mutableStateOf(false) }
    var passwordVisible by remember { mutableStateOf(false) }
    var confirmPasswordVisible by remember { mutableStateOf(false) }

    LaunchedEffect(initialToken) {
        if (!initialToken.isNullOrBlank()) {
            token = initialToken
        }
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(strings.passwordResetTitle) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(Icons.Filled.ArrowBack, contentDescription = strings.back)
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = EventCardBackground,
                    titleContentColor = EventCardAccentDark,
                    navigationIconContentColor = EventCardAccentDark
                )
            )
        },
        containerColor = EventCardBackground
    ) { paddingValues ->
        val focusManager = LocalFocusManager.current
        Column(
            modifier = Modifier
                .fillMaxSize()
                .background(eventBackgroundBrush())
                .padding(paddingValues)
                .padding(16.dp)
                .pointerInput(Unit) {
                    detectTapGestures(onTap = { focusManager.clearFocus() })
                }
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    text = strings.passwordResetDescription,
                    style = MaterialTheme.typography.bodyMedium
                )
                OutlinedTextField(
                    value = email,
                    onValueChange = {
                        email = it
                        requestError = null
                        requestMessage = null
                    },
                    label = { Text(strings.email) },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                    modifier = Modifier.fillMaxWidth(),
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                if (requestError != null) {
                    Text(
                        text = requestError.orEmpty(),
                        color = EventCardAlert,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (requestMessage != null) {
                    Text(
                        text = requestMessage.orEmpty(),
                        color = EventCardAccentDark,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                EventPrimaryButton(
                    text = strings.sendResetLink,
                    onClick = {
                        if (email.isBlank() || !email.contains("@")) {
                            requestError = strings.invalidEmail
                            return@EventPrimaryButton
                        }
                        isRequestLoading = true
                        scope.launch {
                            authRepository.requestPasswordReset(email.trim())
                                .onSuccess {
                                    requestError = null
                                    requestMessage = strings.passwordResetEmailSent(email.trim())
                                }
                                .onFailure { error ->
                                    requestMessage = null
                                    requestError = error.message ?: strings.loginError
                                }
                            isRequestLoading = false
                        }
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(48.dp),
                    enabled = !isRequestLoading,
                    isLoading = isRequestLoading
                )
            }

            EventSurfaceCard(
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(
                    text = strings.passwordResetSetNewPassword,
                    style = MaterialTheme.typography.titleMedium
                )
                Text(
                    text = strings.passwordResetTokenDescription,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                OutlinedTextField(
                    value = token,
                    onValueChange = {
                        token = it
                        resetError = null
                    },
                    label = { Text(strings.passwordResetTokenLabel) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                OutlinedTextField(
                    value = newPassword,
                    onValueChange = {
                        newPassword = it
                        resetError = null
                        resetMessage = null
                    },
                    label = { Text(strings.newPassword) },
                    singleLine = true,
                    visualTransformation = if (passwordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                    trailingIcon = {
                        IconButton(onClick = { passwordVisible = !passwordVisible }) {
                            Icon(
                                imageVector = if (passwordVisible) Icons.Filled.Visibility else Icons.Filled.VisibilityOff,
                                contentDescription = if (passwordVisible) "Hide password" else "Show password"
                            )
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                OutlinedTextField(
                    value = confirmPassword,
                    onValueChange = {
                        confirmPassword = it
                        resetError = null
                        resetMessage = null
                    },
                    label = { Text(strings.confirmPassword) },
                    singleLine = true,
                    visualTransformation = if (confirmPasswordVisible) VisualTransformation.None else PasswordVisualTransformation(),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                    trailingIcon = {
                        IconButton(onClick = { confirmPasswordVisible = !confirmPasswordVisible }) {
                            Icon(
                                imageVector = if (confirmPasswordVisible) Icons.Filled.Visibility else Icons.Filled.VisibilityOff,
                                contentDescription = if (confirmPasswordVisible) "Hide password" else "Show password"
                            )
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    shape = androidx.compose.foundation.shape.RoundedCornerShape(EventCardInputCorner),
                    colors = eventOutlinedFieldColors()
                )
                if (resetError != null) {
                    Text(
                        text = resetError.orEmpty(),
                        color = EventCardAlert,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                if (resetMessage != null) {
                    Text(
                        text = resetMessage.orEmpty(),
                        color = EventCardAccentDark,
                        style = MaterialTheme.typography.bodySmall
                    )
                }
                EventPrimaryButton(
                    text = strings.resetPasswordAction,
                    onClick = {
                        if (token.isBlank() || newPassword.isBlank() || confirmPassword.isBlank()) {
                            resetError = strings.fillAllFields
                            return@EventPrimaryButton
                        }
                        if (newPassword != confirmPassword) {
                            resetError = strings.passwordsDontMatch
                            return@EventPrimaryButton
                        }
                        isResetLoading = true
                        scope.launch {
                            authRepository.resetPassword(token.trim(), newPassword)
                                .onSuccess {
                                    resetError = null
                                    resetMessage = strings.passwordResetSuccess
                                    newPassword = ""
                                    confirmPassword = ""
                                }
                                .onFailure { error ->
                                    resetMessage = null
                                    resetError = error.message ?: strings.loginError
                                }
                            isResetLoading = false
                        }
                    },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(48.dp),
                    enabled = !isResetLoading,
                    isLoading = isResetLoading
                )
            }

            EventTextAction(
                onClick = onNavigateBack,
                modifier = Modifier.align(Alignment.CenterHorizontally),
                text = strings.backToLogin
            )
        }
    }
}
